import { useCallback, useContext, useMemo } from 'react';
import useApiCall from 'hooks/use-api-call';
import useInvestorMe from 'hooks/use-investor-me';
import { DocumentsApi, InvestorsApi, PatchedInvestorUpdateRequest } from 'api';
import { AnyServerError } from 'hooks/use-api-error';
import { TaxInformationFields } from 'libraries/wizard/components/tax-information/tax-information-form';
import WizardContext from 'src/libraries/wizard/wizard-context';

const useTaxInformation = (): {
  onSubmit: (values: TaxInformationFields) => void;
  showLoading: boolean;
  error?: AnyServerError;
} => {
  const { investmentId, resourceId: invitationId, finalize } = useContext(WizardContext);

  const { error: apiError, loading: apiLoading, makeAuthenticatedApi, withApi } = useApiCall();

  const { investor, loading: investorLoading, error: meError } = useInvestorMe();

  const showLoading = investorLoading || apiLoading;
  const error = apiError || meError;

  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback(
    (fieldsValues: TaxInformationFields) => {
      withApi(async () => {
        if (!investor?.id) return false;

        const params: PatchedInvestorUpdateRequest = {
          taxInformation: {
            isSubjectToGermanTax: fieldsValues.isSubjectToGermanTax,
            taxIdentificationNumber: fieldsValues.taxIdentificationNumber,
            nonAssessmentCertificate: fieldsValues.hasNonAssessmentCertificate,
          },
        };

        if (fieldsValues.nonAssessmentCertificate) {
          const docResponse = await documentsApi.documentsCreate({
            file: fieldsValues.nonAssessmentCertificate.file!,
          });
          params.taxInformation!.nonAssessmentCertificateDocId = docResponse.id;
        }
        await investorsApi.investorsPartialUpdate({
          id: investor.id,
          patchedInvestorUpdateRequest: params,
        });

        finalize();
      });
    },
    [investor?.id, investorsApi, documentsApi, withApi, finalize],
  );

  return useMemo(
    () => ({
      showLoading,
      error,
      onSubmit,
    }),
    [onSubmit, investmentId, invitationId, showLoading, error],
  );
};

export default useTaxInformation;
